import { useCallback, useContext } from "react";

import {
  resolveOrganization,
  useOrgFromUrl,
  useUserOrganizations,
} from "./utils";

import { authContext } from "context/auth";
import type { GetUserOrganizationsQuery } from "gql/graphql";
import { useStoredState } from "storage/state";

interface IInitialOrganizationResult {
  data: {
    organizations: string[];
  };
  orgName: string;
  setOrgName: (name: string) => void;
  loading: boolean;
}

/** Determines the organization to show by default  */
const useInitialOrganization = (): IInitialOrganizationResult => {
  const orgFromUrl = useOrgFromUrl();
  const { userEmail } = useContext(authContext);

  const [lastOrganization, setLastOrganization] = useStoredState(
    "organizationV2",
    { deleted: false, name: "" },
    "local",
  );
  const orgName = orgFromUrl ?? lastOrganization.name;
  const setOrgName = useCallback(
    (name: string): void => {
      setLastOrganization({ deleted: false, name });
    },
    [setLastOrganization],
  );

  const handleComplete = useCallback(
    (data: GetUserOrganizationsQuery): void => {
      if (data.me.organizations.length > 0) {
        const resolvedOrg = resolveOrganization(
          orgFromUrl,
          data.me.organizations,
        );
        setOrgName(resolvedOrg);
      } else {
        setOrgName("");
      }
    },
    [orgFromUrl, setOrgName],
  );

  const { data, loading } = useUserOrganizations(handleComplete);

  return {
    data: {
      organizations:
        data?.me.organizations.map((org): string => org.name) ?? [],
    },
    loading: loading || orgName === "" || !userEmail,
    orgName,
    setOrgName,
  };
};

export { useInitialOrganization };
